import Button, {
  ButtonSizeEnum,
  ButtonTypeEnum,
} from "@/components/atoms/Button";
import { MARKETING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import {
  GOOGLE_ADS_CONVERSION_ID_INDIA,
  GOOGLE_ADS_CONVERSION_LABEL_INDIA,
} from "@/shared/constants/env/google.constants";
import { QUERY_PARAMS } from "@/shared/constants/storage.constants";
import { IS_TIMEZONE_INDIA } from "@/shared/constants/timezone.constants";
import { signupCopies_en } from "@/shared/data/navbar.data";
import Amplitude from "@/shared/services/amplitude.service";
import useGlobalStore from "@/shared/store/GraphyHome/useGlobalStore";
import { useDataStore } from "@/shared/store/useDataStore";
import useSignUpStore from "@/shared/store/useSignUpStore";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { getCalendlyLink } from "@/shared/utils/route.util";
import Snackbar from "@/shared/utils/snackbar.util";
import { getFromLocal } from "@/shared/utils/storage.util";
import { INPUT_TYPES, Input } from "@graphy/ui";
import { SNACKBAR_TYPE } from "@shared-types/snackbar";
import isBrowser from "@shared-utils/next.util";
import { allowedUTMParamsList, getUTMFieldsFromLocalStorage, stringifyUTMParams } from "@shared-utils/utm.util";
import axios from "axios";
import * as EmailValidator from "email-validator";
import Head from "next/head";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ModalWithCalendly } from "../MarketingPage/CalendlyWidget";
import InputWithTitleAndChecks from "../molecules/FormElements/InputWithTitleAndChecks";
import SignupPhoneInput from "./SignUpPhoneInput";

const InputWithTitleAndChecksComp = InputWithTitleAndChecks as any;

// type LeadGenerationInputsProps = {
//   header?: boolean;
//   isFooter?: boolean;
//   footerEvent?: boolean;
//   emailOnly?: boolean;
//   validateMobile?: boolean;
//   defaultCountry?: string;
//   ctaText?: string;
//   fullForm?: boolean;
//   placeholderText?: string | object;
// };

type LeadGenerationInputsProps = {
  fullForm?: boolean; // if true, header, emailOnly is ignored
  header?: boolean;
  isFooter?: boolean;
  footerEvent?: boolean;
  emailOnly?: boolean;
  validateMobile?: boolean;
  defaultCountry?: string;
  ctaText?: string;
  placeholderText?: string | object;
  sourceSent?: string;
  scenesKey?: string;
};

const LeadGenerationInputs: React.FC<LeadGenerationInputsProps> = ({
  header,
  isFooter,
  footerEvent,
  emailOnly,
  validateMobile,
  defaultCountry,
  ctaText = "Schedule a demo",
  fullForm,
  placeholderText,
  sourceSent,
  scenesKey,
}) => {
  const [email, setEmail] = useState<string>("");
  const [fullname, setFullname] = useState<string>("");
   // const [mobileNumber, setMobileNumber] = useState<string>("");
  const [isPopupVisible, setPopupVisible] = useState(false);
  const ipInfo = useGlobalStore((state) => state.ipInfo);
  const defaultCountryCode = ipInfo?.country?.toLowerCase();
  const {
    contactNumber: mobileNumber,
    setContactNumber: setMobileNumber,
    callingCode,
  } = useSignUpStore();
  const path = usePathname();
  const emailRef = useRef(email);
  const fullnameRef = useRef(fullname);
  const mobileNumberRef = useRef<string>("");

  useEffect(() => {
    emailRef.current = email;
  }, [email]);

  useEffect(() => {
    fullnameRef.current = fullname;
  }, [fullname]);

  const { countryCode } = useDataStore();

  useEffect(() => {
    mobileNumberRef.current = mobileNumber;
  }, [mobileNumber]);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const snackbar = new Snackbar();
  const router = useRouter();

  let utmFields = getUTMFieldsFromLocalStorage();

  const {
    setScheduleDemoSuccessEmail,
    setScheduleDemoSuccessName,
    setShowScheduleDemoSuccessModal,
    setScenesKeyAvailable,
  } = useGlobalStore();
  useEffect(() => {
    if (scenesKey) setScenesKeyAvailable(scenesKey);
  }, [scenesKey]);


  const reset = useCallback(() => {
    setFullname("");
    setEmail("");
    const countryCode = mobileNumber?.split(" ")?.[0] || "+91";

    setMobileNumber(countryCode);
  }, [mobileNumber]);

  const calendlyLink = getCalendlyLink(countryCode)

  const openCalendlyWithUtm = useCallback((email: string, calendlyLink: string) => {
    let utmFields = getUTMFieldsFromLocalStorage();
    const currentUrlParams = new URLSearchParams(window.location.search);
    
    allowedUTMParamsList.forEach(param => {
      const value = currentUrlParams.get(param);
      if (value) {
        utmFields[param] = value;
      }
    });
    
    const utmParams = stringifyUTMParams(utmFields);
    window.open(`${calendlyLink}?email=${email}&${utmParams}`, "_blank");
  }, []);

  const createHubspotContact = useCallback(() => {
    const requestBody = {
      email,
      ...utmFields,
    } as any;
    const queryParams = getFromLocal(QUERY_PARAMS);

    if (mobileNumber) {
      requestBody.phone = `${callingCode}${mobileNumber}`;
    }
    if (fullname) {
      requestBody.firstname = fullname;
    }
    if (isBrowser()) {
      requestBody.subdomain = window.location.host;
    }

    if (queryParams && typeof queryParams === "object") {
      if (queryParams.gclid) {
        requestBody.gclid = queryParams.gclid;
      }
      if (queryParams.matchtype) {
        requestBody.matchtype = queryParams.matchtype;
      }
      if (queryParams.device) {
        requestBody.device = queryParams.device;
      }
    }

    try {
      axios
        .post(`/api/hubspot`, requestBody)
        .then(() => {
          setShowScheduleDemoSuccessModal(true);
          setScheduleDemoSuccessEmail(email || "");
          setScheduleDemoSuccessName(fullname || "");
          router.query.schedule_demo = "success";
          router.push(router);
          reset();
        })
        .catch(() => {
          openCalendlyWithUtm(email, calendlyLink)
        });
    } catch (e) {
      openCalendlyWithUtm(email, calendlyLink)
  }
  }, [
    email,
    fullname,
    calendlyLink,
    mobileNumber,
    reset,
    router,
    setScheduleDemoSuccessEmail,
    setScheduleDemoSuccessName,
    setShowScheduleDemoSuccessModal,
    utmFields,
  ]);

  const createScenesContact = useCallback(() => {
    const requestBody = {
      email,
      ...utmFields,
    } as any;
    const queryParams = getFromLocal(QUERY_PARAMS);

    if (mobileNumber) {
      requestBody.phone = `+${callingCode}${mobileNumber}`;
    }
    if (fullname) {
      requestBody.firstname = fullname;
    }
    if (isBrowser()) {
      requestBody.subdomain = window.location.host;
    }

    if (queryParams && typeof queryParams === "object" && queryParams.gclid) {
      requestBody.gclid = queryParams.gclid;
    }

    // router.push("https://google.com");

    try {
      axios
        .post(scenesKey as string, requestBody)
        .then(() => {
          setShowScheduleDemoSuccessModal(true);
          setScheduleDemoSuccessEmail(email || "");
          setScheduleDemoSuccessName(fullname || "");
          router.query.schedule_demo = "success";
          router.push(router);
          reset();
        })
        .catch(() => {
          openCalendlyWithUtm(email, calendlyLink)
        });
    } catch (e) {
      openCalendlyWithUtm(email, calendlyLink);
    }
  }, [
    email,
    fullname,
    calendlyLink,
    mobileNumber,
    reset,
    router,
    setScheduleDemoSuccessEmail,
    setScheduleDemoSuccessName,
    setShowScheduleDemoSuccessModal,
    utmFields,
    scenesKey,
  ]);

  const handleScheduleDemo = useCallback(() => {
    const {
      utm_params,
      referrer,
      screen_width,
      source,
      name,
      email,
      phone_country,
      phone_number,
      current_url,
    } = MARKETING_PAGE_EVENTS.REQUEST_A_DEMO_FORM_FILLED.PROPERTIES;

    new Amplitude().sendEvent({
      type: MARKETING_PAGE_EVENTS.REQUEST_A_DEMO_FORM_FILLED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [referrer]: getReferrer(),
        [name]: fullname,
        [email]: email,
        [phone_number]: mobileNumber,
        [phone_country]: callingCode,
        [source]: sourceSent,
      } as { [key: string]: string },
    });
    const countryCode = mobileNumber?.split(" ")?.[0] || "+91";
    const mobileValidation = Boolean(
      mobileNumber && mobileNumber.length > 0 && mobileNumber !== countryCode
    );
    const emailValidation = Boolean(email && email.length > 0);
    const fullNameValidation = Boolean(fullname && fullname.length > 0);


    if (path.includes("online-course-platform")) {
      if (emailValidation && fullNameValidation) {
        setPopupVisible(true);
        window.history.pushState({}, '', window.location.href + '?demo_booked=');
      }
      return;
    }
    
    if (scenesKey) {
      createScenesContact();
    } else {
      createHubspotContact();
    }

    return;
    if (
      (emailOnly && emailValidation) ||
      (validateMobile && mobileValidation && emailValidation) ||
      (!validateMobile && emailValidation)
    ) {
      if (email && !EmailValidator.validate(email)) {
        return snackbar.show("Enter a valid email", SNACKBAR_TYPE.ERROR);
      } else {
        const isCountryIndia = IS_TIMEZONE_INDIA;
        if (isCountryIndia) {
          (window as any).gtag("event", "conversion", {
            send_to: `${GOOGLE_ADS_CONVERSION_ID_INDIA}/${GOOGLE_ADS_CONVERSION_LABEL_INDIA}`,
          });
        }
        // else {
        //   (window as any).gtag('event', 'conversion', {
        //     send_to: `${GOOGLE_ADS_CONVERSION_ID_ABROAD}/${GOOGLE_ADS_CONVERSION_LABEL_ABROAD}`
        //   });
        // }
      }
      createHubspotContact();
      const currentUrl = window?.location.href;
      // sendAmplitudeData("schedule_demo_tapped_landing_page", {
      //   phone_number: mobileNumber || "",
      //   email,
      //   source: footerEvent ? "footer" : header ? "hero" : "body",
      //   UTM_params: utmProperties,
      //   current_url: currentUrl,
      // });
      // sendAnalyticsEvent("graphy", GOOGLE_EVENTS.SCHEDULE_DEMO.FORM_FILLED);
      // sendGoogleTagEvent({
      //   eventName: GOOGLE_EVENTS.SCHEDULE_DEMO.FORM_FILLED,
      //   gtmId: GTM_ID,
      //   isRootDataLayer: true,
      // });
    } else {
      snackbar.show(
        emailOnly || !validateMobile
          ? "Please enter email."
          : "Please enter both mobile number & email.",
        SNACKBAR_TYPE.ERROR
      );
    }
  }, [
    createHubspotContact,
    email,
    emailOnly,
    mobileNumber,
    snackbar,
    validateMobile,
  ]);

  const closeModal = () => {
    setPopupVisible(false);
    setFullname("");
    setEmail("");
    setMobileNumber("");
    const newUrl = window.location.href.replace(/\?demo_booked=(success)?/, '');
    window.history.pushState({}, '', newUrl);

  };

  const renderFullForm = useCallback(() => {
    return (
      <div className="max-w-base bottom-shadow-purple-xl flex w-full flex-col rounded-xl border border-grey-2 p-6 lg:bg-white">
        <Head>
          {IS_TIMEZONE_INDIA && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            window.gtag = gtag;
            gtag('config','${GOOGLE_ADS_CONVERSION_ID_INDIA}');`,
              }}
              defer
            />
          )}
          {/* {!IS_TIMEZONE_INDIA && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            window.gtag = gtag;
            gtag('config','${GOOGLE_ADS_CONVERSION_ID_ABROAD}');`
              }}
              defer
            />
          )} */}
        </Head>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <h5 className="hidden text-sm font-semibold text-slate01 lg:block">
                Name
              </h5>
              {/* <InputWithTitleAndChecksComp
          value={fullname}
          placeholder="Enter your name"
          placeholderColor="placeholder-slate03"
          name="title"
          onChange={(e: any) => setFullname(e.target.value)}
          className={`focus:border-graphite01 mb-4 pb-px focus:outline-none focus:ring-0 lg:mb-9`}
          styleType="sleek"
          type="text"
        /> */}
              <Input
                containerMargin=""
                className="font-body flex h-10 w-full rounded border border-grey-2 px-2.5 py-3"
                type={INPUT_TYPES.TEXT}
                inputLimit={200}
                // errorMessage={signInInputsErrors?.email}
                onChange={(value) => setFullname(value as string)}
                value={fullname}
                placeholder="Enter your name"
              />
            </div>
            <div className="flex flex-col gap-3">
              <h5 className="hidden text-sm font-semibold text-slate01 lg:block">
                Email address
              </h5>
              <Input
                containerMargin=""
                className="font-body flex h-10 w-full rounded border border-grey-2 px-2.5 py-3"
                type={INPUT_TYPES.EMAIL}
                inputLimit={200}
                // errorMessage={signInInputsErrors?.email}
                onChange={(value) => setEmail(value as string)}
                value={email}
                placeholder="Enter your email"
              />
              {/* <InputWithTitleAndChecksComp
              value={email}
              placeholder={placeholderText ?? "Enter your email"}
              placeholderColor="placeholder-slate03"
              name="title"
              onChange={(e: any) => setEmail(e.target.value)}
              className={`focus:border-graphite01 rounded-lg pb-px focus:outline-none focus:ring-0`}
              styleType="sleek"
              type="email"
            /> */}
            </div>
            <div className="flex flex-col gap-3">
              <h5 className="hidden text-sm font-semibold text-slate01 lg:block">
                Mobile Number
              </h5>
              <SignupPhoneInput signupCopies={signupCopies_en} />
              {/* <MobileInput
            value={mobileNumber}
            onChange={(phone: string) => setMobileNumber(phone)}
            isFormattedValue={true}
            required={false}
            defaultCountryCode={defaultCountryCode || defaultCountry}
            styleType="sleek"
          /> */}
            </div>
          </div>

          {/* <CtaButtonGroup
          ctas={[
            {
              type: ButtonTypeEnum.Primary,
              size: ButtonSizeEnum.Regular,
              text: "Request a Demo",
              actionType: 
            }
          ]}
        /> */}
          <Button
            onClick={handleScheduleDemo}
            // className={`w-full items-center lg:w-48 ${
            //   emailOnly ? "mx-auto" : ""
            // } h-12 rounded
            //     bg-cta-primary text-title-14 text-grey-0 md:mt-0`}
          >
            {ctaText}
          </Button>
          {isPopupVisible && (
            <ModalWithCalendly showModal={isPopupVisible} onClose={closeModal} userEmail={email}
              userName={fullname} MobileNumber={mobileNumber} />
          )}
        </div>
      </div>
    );
  }, [fullname, email, ctaText, handleScheduleDemo, isPopupVisible]);

  const renderOldForm = useCallback(() => {
    return (
      <>
        <div
          className={`flex ${
            header
              ? "flex-col"
              : "flex-col items-center justify-center md:flex-row"
          }  w-full md:space-x-10`}
        >
          <Input
            containerMargin=""
            className="font-body flex h-10 w-full rounded border border-grey-2 px-2.5 py-3"
            type={INPUT_TYPES.EMAIL}
            inputLimit={200}
            // errorMessage={signInInputsErrors?.email}
            onChange={(value) => setEmail(value as string)}
            value={email}
            placeholder="Enter your email"
          />
          {!header && (
            <div className="w-full md:w-64 md:px-0">
              <SignupPhoneInput signupCopies={signupCopies_en} />
            </div>
          )}
        </div>
        <div className="mt-2 flex w-full flex-col items-center justify-center gap-3 md:flex-row">
          {header && (
            <div className=" flex-grow">
              <SignupPhoneInput signupCopies={signupCopies_en} />
            </div>
          )}
          {!header && (
            <div className="whitespace-nowrap">
              <Button onClick={() => handleScheduleDemo()}>{ctaText}</Button>
            </div>
          )}
        </div>
        {header && (
          <div className="mt-2 grid self-stretch whitespace-nowrap">
            <Button
              onClick={() => handleScheduleDemo()}
              type={ButtonTypeEnum.Primary}
              size={ButtonSizeEnum.Regular}
            >
              {ctaText}
            </Button>
          </div>
        )}
      </>
    );
  }, [header, email, ctaText, handleScheduleDemo]);

  return (
    <div
      className={`flex w-full flex-col items-center justify-center ${
        isFooter ? "" : ""
      } `}
    >
      {fullForm ? renderFullForm() : renderOldForm()}
    </div>
  );
};

LeadGenerationInputs.defaultProps = {
  header: false,
  isFooter: false,
  emailOnly: false,
  validateMobile: true,
  defaultCountry: "us",
  fullForm: false,
};

export default LeadGenerationInputs;
